import { Controller } from "stimulus";

export default class extends Controller {
  static values = {
    orderCostMinimum: Number,
    currentTotalCost: Number,
  };

  static targets = [
    "selectionCheckbox",
    "selectionSummaryText",
    "quantitySelectField",
    "hiddenPurchasePolicyField",
    "subscriptionLabel",
    "hiddenQuantityField",
    "hiddenPriceField",
    "formSubmit",
    "remainderText",
    "progressBar",
    "subscribeCheckbox",
  ];

  connect() {
    this._updateFormState();
  }

  quantitySelectFieldTargetConnected(qtySelectField) {
    const productSku = qtySelectField.dataset.productSku;
    this._setQtySelectFieldToHiddenQty(productSku, qtySelectField);
  }

  subscribeItem(event) {
    const checkbox = event.target;
    const productSku = checkbox.dataset.productSku;
    const purchasePolicyId = checkbox.dataset.purchasePolicyId;
    const subscriptionPurchasePolicyId =
      checkbox.dataset.subscriptionPurchasePolicyId;
    const specialPrice = checkbox.dataset.specialPrice;
    const subscriptionSpecialPrice = checkbox.dataset.subscriptionSpecialPrice;
    const checked = checkbox.checked;

    if (checked) {
      for (const hiddenPurchasePolicyField of this
        .hiddenPurchasePolicyFieldTargets) {
        if (hiddenPurchasePolicyField.dataset.productSku == productSku) {
          hiddenPurchasePolicyField.value = subscriptionPurchasePolicyId;
        }
      }
      for (const subscriptionLabel of this.subscriptionLabelTargets) {
        if (subscriptionLabel.dataset.productSku == productSku) {
          subscriptionLabel.classList.remove("hidden");
        }
      }
      for (const hiddenPriceField of this.hiddenPriceFieldTargets) {
        if (hiddenPriceField.dataset.productSku == productSku) {
          hiddenPriceField.value = subscriptionSpecialPrice;
        }
      }
    } else {
      for (const hiddenPurchasePolicyField of this
        .hiddenPurchasePolicyFieldTargets) {
        if (hiddenPurchasePolicyField.dataset.productSku == productSku) {
          hiddenPurchasePolicyField.value = purchasePolicyId;
        }
      }

      for (const subscriptionLabel of this.subscriptionLabelTargets) {
        if (subscriptionLabel.dataset.productSku == productSku) {
          subscriptionLabel.classList.add("hidden");
        }
      }

      for (const hiddenPriceField of this.hiddenPriceFieldTargets) {
        if (hiddenPriceField.dataset.productSku == productSku) {
          hiddenPriceField.value = specialPrice;
        }
      }
    }

    this._updateTotal();
  }

  addItem(event) {
    const qtySelectField = event.target;
    const productSku = qtySelectField.dataset.productSku;
    const quantity = qtySelectField.options[qtySelectField.selectedIndex].value;
    if (quantity > 0) {
      this._showSelection(productSku, quantity);
    } else {
      this._hideSelection(productSku);
    }
    this._setHiddenQtyTo(productSku, quantity);

    this._updateTotal();
  }

  removeItem(event) {
    let productSku = event.target.dataset.productSku;
    for (const quantitySelectField of this.quantitySelectFieldTargets) {
      if (quantitySelectField.dataset.productSku == productSku) {
        quantitySelectField.options[0].selected = true;
      }
    }
    for (const subscribeCheckbox of this.subscribeCheckboxTargets) {
      if (subscribeCheckbox.dataset.productSku == productSku) {
        if (subscribeCheckbox.checked) {
          subscribeCheckbox.checked = false;
          subscribeCheckbox.dispatchEvent(new Event("change"));
        }
      }
    }

    this._hideSelection(productSku);
    this._setHiddenQtyTo(productSku, 0);

    this._updateTotal();
  }

  _updateTotal() {
    this.currentTotalCostValue = 0;
    let addedProducts = {};

    for (const hiddenQuantityField of this.hiddenQuantityFieldTargets) {
      let productSku = hiddenQuantityField.dataset.productSku;
      if (!(productSku in addedProducts)) {
        for (const hiddenPriceField of this.hiddenPriceFieldTargets) {
          if (hiddenPriceField.dataset.productSku == productSku) {
            let productValue =
              hiddenPriceField.value * (hiddenQuantityField.value || 0);
            this.currentTotalCostValue += productValue;
            addedProducts[productSku] = productValue;
            break;
          }
        }
      }
    }

    this._updateFormState();
    this._updateRemainerText();
    this._updateProgressBar();
  }

  _updateFormState() {
    if (this.currentTotalCostValue >= this.orderCostMinimumValue) {
      this._enableFormSubmission();
    } else {
      this._disableFormSubmission();
    }
  }

  _enableFormSubmission() {
    for (const formSubmitTarget of this.formSubmitTargets) {
      formSubmitTarget.removeAttribute("disabled", "disabled");
    }
  }

  _disableFormSubmission() {
    for (const formSubmitTarget of this.formSubmitTargets) {
      formSubmitTarget.setAttribute("disabled", "disabled");
    }
  }

  _setQtySelectFieldToHiddenQty(productSku, qtySelectField) {
    for (const hiddenQuantityField of this.hiddenQuantityFieldTargets) {
      if (hiddenQuantityField.dataset.productSku == productSku) {
        this._setSelectFieldToValue(qtySelectField, hiddenQuantityField.value);
        break;
      }
    }
  }

  _setSelectFieldToValue(qtySelectField, rawVal) {
    const value = this._isBlank(rawVal) ? "0" : rawVal;
    let index = null;
    for (const option of qtySelectField.options) {
      if (option.value == value) {
        index = option.index;
        break;
      }
    }
    if (qtySelectField.options[index]) {
      qtySelectField.options[index].selected = true;
    }
  }

  _isBlank(val) {
    return !val;
  }

  _showSelection(productSku, quantity) {
    for (const selection of this.selectionCheckboxTargets) {
      if (selection.dataset.productSku == productSku) {
        this._updateQuantityShown(productSku, quantity);
        selection.checked = true;
        selection.dataset.quantity = quantity;
      }
    }
  }

  _updateRemainerText() {
    let remainingValue =
      this.orderCostMinimumValue - this.currentTotalCostValue;

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    const formattedNumber = formatter.format(remainingValue);

    for (const target of this.remainderTextTargets) {
      if (remainingValue <= 0) {
        target.innerHTML = `Congrats! You've met the order minimum.`;
      } else {
        target.innerHTML = `${formattedNumber} away from minimum order value.`;
      }
    }
  }

  _updateProgressBar() {
    let percentage = Math.min(
      (this.currentTotalCostValue / this.orderCostMinimumValue) * 100,
      100,
    );
    for (const target of this.progressBarTargets) {
      target.style.width = `${percentage}%`;
    }
  }

  _updateQuantityShown(productSku, quantity) {
    for (const target of this.selectionSummaryTextTargets) {
      if (target.dataset.productSku == productSku) {
        let text = target.innerHTML;
        let updatedText = text
          .replace(/\([\d{0,2}]*\)/g, "()")
          .replace("()", `(${quantity})`);
        target.innerHTML = updatedText;
      }
    }
  }

  _hideSelection(productSku) {
    var eventDispatched = false;
    for (const selection of this.selectionCheckboxTargets) {
      if (selection.dataset.productSku == productSku) {
        selection.checked = false;
        selection.dataset.quantity = 0;

        if (!eventDispatched) {
          selection.dispatchEvent(new Event("click"));
          eventDispatched = true;
        }
      }
    }
  }

  _setHiddenQtyTo(productSku, value) {
    for (const hiddenQuantityField of this.hiddenQuantityFieldTargets) {
      if (hiddenQuantityField.dataset.productSku == productSku) {
        hiddenQuantityField.value = value;
      }
    }
  }
}
